import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner } from '@mylighthouse/prism-ember';
import type { BannerStyle } from '@mylighthouse/prism-ember/types/components/prism/banner';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { tKey } from 'ember-intl';
import type { BudgetForecastDataItem } from 'lumen/models';
import {
  itemHasChildWithDataForHiddenSegment,
  itemHasDataAndAllChildrenHaveNoData,
  itemHasUnmappedChildWithData,
} from 'lumen/utils/budget-forecast/force-distribution';

const TRANSLATION_KEYS = {
  MULTIPLE: tKey(
    'settings.budget_forecast.banner.multiple_distribution_issues',
  ),
  UNMAPPED_SEGMENTS: tKey('settings.budget_forecast.banner.unmapped_segments'),
  EXCLUDED_SEGMENT: {
    budget: tKey('settings.budget_forecast.banner.budget.excluded_segment'),
    forecast: tKey('settings.budget_forecast.banner.forecast.excluded_segment'),
  },
  LEVEL_ADDED: {
    budget: tKey(
      'settings.budget_forecast.banner.budget.segment_granularity_level_added',
    ),
    forecast: tKey(
      'settings.budget_forecast.banner.forecast.segment_granularity_level_added',
    ),
  },
} as const;

export interface SettingsBudgetForecastBannerForceDistributeSignature {
  Args: {
    itemsToBeForceDistributed: BudgetForecastDataItem[];
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastBannerForceDistribute extends Component<SettingsBudgetForecastBannerForceDistributeSignature> {
  @service declare intl: Services['intl'];

  get bannerProperties(): {
    bannerMessage: string;
    bannerStyle?: BannerStyle;
    icon: IconName;
  } | null {
    if (this.hasMultipleIssues) {
      return {
        bannerMessage: this.intl.t(TRANSLATION_KEYS.MULTIPLE),
        bannerStyle: 'warning',
        icon: 'warning',
      };
    }

    const distributionIssues = this.distributionIssues;

    if (distributionIssues.hasUnmappedSegmentsWithData) {
      return {
        bannerMessage: this.intl.t(TRANSLATION_KEYS.UNMAPPED_SEGMENTS),
        bannerStyle: 'warning',
        icon: 'warning',
      };
    }

    if (distributionIssues.hasHiddenSegmentsWithData) {
      return {
        bannerMessage: this.intl.t(
          TRANSLATION_KEYS.EXCLUDED_SEGMENT[this.args.view],
        ),
        icon: 'circle-information-outline',
      };
    }

    if (distributionIssues.hasSegmentGranularityLevelAdded) {
      return {
        bannerMessage: this.intl.t(
          TRANSLATION_KEYS.LEVEL_ADDED[this.args.view],
        ),
        icon: 'circle-information-outline',
      };
    }

    return null;
  }

  get distributionIssues(): {
    hasHiddenSegmentsWithData: boolean;
    hasSegmentGranularityLevelAdded: boolean;
    hasUnmappedSegmentsWithData: boolean;
  } {
    const items = this.args.itemsToBeForceDistributed;

    return {
      hasHiddenSegmentsWithData: items.some(
        itemHasChildWithDataForHiddenSegment,
      ),
      hasSegmentGranularityLevelAdded: items.some(
        itemHasDataAndAllChildrenHaveNoData,
      ),
      hasUnmappedSegmentsWithData: items.some(itemHasUnmappedChildWithData),
    };
  }

  get hasMultipleIssues(): boolean {
    return Object.values(this.distributionIssues).filter(Boolean).length > 1;
  }

  <template>
    {{#if this.bannerProperties}}
      <PrismBanner
        @bannerMessage={{this.bannerProperties.bannerMessage}}
        @bannerStyle={{this.bannerProperties.bannerStyle}}
        @icon={{this.bannerProperties.icon}}
        @isOneLine={{true}}
      />
    {{/if}}
  </template>
}
